import { type ReactNode, useMemo, useEffect, type RefObject, useRef } from 'react'
import { HeaderMain } from '../header/HeaderMain';
import { ChatCompose } from '../chat';
import { useRouter } from 'next/router';
import { modelPopupAtom } from '../store/instruction';
import { useAtom, useSetAtom } from 'jotai';
import { InstructionsPopup } from '../popup/InstructionPopup';
import { PromptPopup } from '../popup/PromptPopup';
import { MainLeftMenu } from '../header/LeftMenu';
import { PopupSearchNew } from '../popup/SearchPopupNew';
import { isSearchAtom } from '../store/util';
import { UpgradeModalNew } from '../popup/UpgradePopupNew';
import { ApiKeyModal } from '../popup/ApiKeyPopup';
import { BiChevronDown } from '@acme/ui';
import { useScrollToBottom } from '../hooks/useScrollToBottom';
import { useModelDeepLink } from '../hooks/useModelDeepLink';
import { FileAttachPopup } from '../popup/FileAttachPopup';

type LayoutMainProps = {
  title: ReactNode;
  titleMenu: ReactNode;
  children: ReactNode;
  leftMenu: ReactNode;
  leftMenuAction: ReactNode;
};

export function LayoutMain(props: LayoutMainProps) {
  const { title, titleMenu, children, leftMenu, leftMenuAction } = props;

  const scrollRef = useRef<HTMLElement>(null);
  const router = useRouter();
  const setSearchPopup = useSetAtom(isSearchAtom)

  const activePath = useMemo<'note' | 'chat' | ''>(() => {
    if (router.pathname === '/' || router.pathname === '/chat') {
      return 'chat';
    } else if (router.pathname === '/note') {
      return 'note';
    } else {
      return '';
    }
  }, [router.pathname]);

  const [isNotBottom, onScrollToBottom] = useScrollToBottom(scrollRef, 300, activePath === 'chat', 100)

  // /chat?model=..&compare=.. links from the model and comparison pages
  useModelDeepLink(activePath === 'chat')

  const isHideMenuContent = useMemo(() => {
    // if /extension/*/**  or /settings/*/** then hide menu content
    if (router.pathname.startsWith('/extension') || router.pathname.startsWith('/settings')) {
      return true;
    }
    return false;
  }, [router.pathname]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' || e.key === 'k') {
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          setSearchPopup(true);
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setSearchPopup]);

  // Useeffect if on mobile and path is chat (/) and has chatId in query then user click back it should prevent go back but open the chat list sidebar
  useEffect(() => {
    if (activePath === 'chat' && router.query.chatId) {
      const handlePopState = (e: PopStateEvent) => {
        e.preventDefault();
        router.push({ pathname: '/chat', query: {} });
      };
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [activePath, router]);

  return (
    <>
      <PopupSearchNew />
      <UpgradeModalNew />
      <ApiKeyModal />
      <div className="flex h-screendvh max-h-screen overflow-hidden">
        {/* Left Navbar */}
        <MainLeftMenu
          action={leftMenuAction}
        >
          {leftMenu}
        </MainLeftMenu>

        {/* Main Content */}
        <div className={`flex flex-col flex-grow relative ${!isHideMenuContent ? 'lg:max-w-[calc(100%-19rem)]' : 'max-w-[calc(100%-3.5rem)]'}  max-w-[100vw]`}>
          {/* Fixed Header */}
          <HeaderMain title={title} className="!absolute !w-[100%] z-30">
            {titleMenu}
          </HeaderMain>

          {/* Content Container */}
          <main className="w-full flex-grow max-h-screen overflow-y-auto relative pt-12 p-3 bg-gray-50 dark:bg-[#0b141a] transition-colors duration-200">
            <div
              className="h-full overflow-y-auto bg-white dark:bg-[#353541] rounded-lg shadow-sm transition-all duration-200 ease-in-out"
              ref={scrollRef as RefObject<HTMLDivElement>}
            >
              {/* Main Content */}
              {children}
              {activePath === 'chat' && <ChatCompose />}
              <Popup />
            </div>

            {(isNotBottom && activePath === 'chat') && (
              <div className="absolute right-3 sm:right-10 bottom-[6.5rem] z-[999999] transition-all duration-200">
                <button
                  className="bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 rounded-full p-2 shadow-lg cursor-pointer transition-all duration-150 transform hover:scale-105"
                  onClick={onScrollToBottom}
                  aria-label="Scroll to bottom"
                >
                  <BiChevronDown className="text-white w-5 h-5 text-xl sm:w-6 sm:h-6 sm:text-2xl" />
                </button>
              </div>
            )}

            {/* Footer */}
            {/* <div className="absolute w-full left-0 bottom-5 flex items-center justify-center">
              <div className="flex flex-row gap-2 items-center justify-between p-4 rounded shadow-sm bg-white w-full lg:w-[35vw]">
              </div>
            </div> */}
          </main>
        </div>
      </div>
    </>
  );
}

const Popup = () => {
  const [modelPopup, setModelPopup] = useAtom(modelPopupAtom)
  return (
    <>
      {(modelPopup.open && modelPopup.type === 'instruction') && <InstructionsPopup />}
      {(modelPopup.open && modelPopup.type === 'prompt') && <PromptPopup />}
      {(modelPopup.open && modelPopup.type === 'attach-file') && <FileAttachPopup />}
    </>
  )
}

